/**
 * Pledges List Card Component
 *
 * Shows a table of all pledges made by the authenticated donor
 */

import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { HandRaisedIcon } from '@heroicons/react/24/outline';
import type { UsePledgesResult } from '@/hooks/usePledges';

interface PledgesListCardProps {
  pledgesData: UsePledgesResult;
}

const STATUS_LABEL: Record<number, string> = {
  1: 'Pending',
  2: 'Confirmed',
  3: 'Cancelled',
};

const STATUS_COLORS: Record<number, string> = {
  1: 'bg-yellow-100 text-yellow-800',
  2: 'bg-green-100 text-green-800',
  3: 'bg-red-100 text-red-800',
};

function fmt(amount: number | null) {
  if (amount === null) return '—';
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function fmtDate(dateStr: string | null) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export const PledgesListCard: FC<PledgesListCardProps> = ({ pledgesData }) => {
  const navigate = useNavigate();
  const { pledges, isLoading, error, totalAmount } = pledgesData;

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">My Pledges</h3>
          <p className="text-sm text-gray-500">All pledge commitments you have recorded</p>
        </div>
        <button
          onClick={() => navigate('/pledge')}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600 hover:text-emerald-700 border border-emerald-200 rounded-lg px-3 py-1.5 hover:bg-emerald-50 transition-colors"
        >
          <HandRaisedIcon className="h-4 w-4" />
          New Pledge
        </button>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-10">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-emerald-600" />
        </div>
      )}

      {/* Error */}
      {!isLoading && error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          Unable to load pledges: {error.message}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && pledges.length === 0 && (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <div className="rounded-full bg-emerald-50 p-4 mb-3">
            <HandRaisedIcon className="h-8 w-8 text-emerald-400" />
          </div>
          <p className="text-gray-600 font-medium">No pledges recorded yet</p>
          <p className="text-gray-400 text-sm mt-1">Make your first pledge to get started.</p>
          <button
            onClick={() => navigate('/pledge')}
            className="mt-4 rounded-lg bg-emerald-600 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-700 transition-colors"
          >
            Make a Pledge
          </button>
        </div>
      )}

      {/* Table */}
      {!isLoading && !error && pledges.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pledged By
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Confirmed
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pledges.map((pledge) => {
                  const statusCode = pledge.statusCode ?? 1;
                  return (
                    <tr key={pledge.pledgeRecordId} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {fmtDate(pledge.datePledged)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {pledge.pledgeBy}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {pledge.pledgeDonationTypeDescription ? (
                          <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
                            {pledge.pledgeDonationTypeDescription}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900 text-right">
                        {fmt(Number(pledge.amountPledged))}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            STATUS_COLORS[statusCode] ?? 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {STATUS_LABEL[statusCode] ?? 'Unknown'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {fmtDate(pledge.dateConfirmed)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Footer total */}
          <div className="mt-4 flex justify-end border-t border-gray-100 pt-3">
            <span className="text-sm text-gray-500 mr-3">Total pledged:</span>
            <span className="text-sm font-bold text-gray-900">{fmt(Number(totalAmount))}</span>
          </div>
        </>
      )}
    </div>
  );
};
